import { Component, Inject, OnInit } from "@angular/core";
import { NavController, AlertController } from "ionic-angular";
import { Store } from "@ngrx/store";
import { AppState } from "../../app/models/app.state";
import * as UserActions from "../../app/store/actions/user.actions";
import { TranslateService } from "@ngx-translate/core";
import {
  EnvironmentVariables,
  ENV_VARS
} from "../../app/environment-variables/environment-variables.module";
import { NavParams } from "ionic-angular/navigation/nav-params";
import { LoadingController } from "ionic-angular/components/loading/loading-controller";
import { AdminService } from "../../app/services/admin.service";
import { IClinicalSuiteIncomingParams } from "../../app/models/types";
import { first } from "rxjs/operators";
import { PatientPage } from "../patient.page/patient.page";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires";
import { DashboardService } from "../../app/services/dashboard.service";
import { DiaprofilPatient } from "../../app/models/diaprofil-patient";
import { HomePage } from "../home.page/home.page";
import { ModalController } from "ionic-angular/components/modal/modal-controller";
import { Events } from "ionic-angular/util/events";
import { CurrentConsultationsPage } from "../current-consultations.page/current-consultations.page";
import { FrontPage } from "../front.page/front.page";
import { DOCUMENT } from "@angular/common";
import { AuthService } from "../../app/services/auth.service";
import { ActionSheetController } from "ionic-angular";

@Component({
  selector: "clinical-suite-page",
  templateUrl: "clinical-suite.page.html"
})
export class ClinicalSuitePage implements OnInit {
  // userUuid={Context:User.Id.UniqueIdentifier.CCS}&ssn={Context:Patient.Id.NationalIdNumber}&encounterStartTime={Context:Encounter.Co.StartTime.CCS}&careEventStartTime={Context:EpisodeOfCareEvent.Co.StartTime.CCS}&systemToken=y9uvewZm6wQFchTvXQLDVRup4RpHnRjG3jGXQ7bS
  // http://localhost:8100/#/clinical-suite/userUuid=Tweb&ssn=2512489996&systemToken=y9uvewZm6wQFchTvXQLDVRup4RpHnRjG3jGXQ7bS
  params: IClinicalSuiteIncomingParams;
  hasParamErrors: boolean;
  paramErrors: string[];
  cleanedParams: {
    params: IClinicalSuiteIncomingParams;
    hasErrors: boolean;
    errors: string[];
  };
  window: Window;
  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public store: Store<AppState>,
    public translate: TranslateService,
    public loadingCtrl: LoadingController,
    public alertCtrl: AlertController,
    public questionnaireResponseService: QuestionnaireResponseService,
    public dashboard: DashboardService,
    public admin: AdminService,
    public modalCtrl: ModalController,
    public actionSheetCtrl: ActionSheetController,
    private events: Events,
    private authService: AuthService,

    @Inject(ENV_VARS) public envVars: EnvironmentVariables,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.window = this.document.defaultView;
  }

  ngOnInit() {
    if (this.authService.jwt) {
      this.authService.resetAppState(false);
    }

    const loading = this.loadingCtrl.create({
      content: "Starter DiaProfil",
      dismissOnPageChange: true
    });
    loading.present();
    const rawParams = this.navParams.get("params");
    this.cleanedParams = this.cleanParams(rawParams, [
      "userUuid",
      "systemToken"
    ]);
    if (this.window.history.replaceState) {
      this.window.history.replaceState(
        {},
        "DiaProfil",
        `${this.window.location.protocol}//${this.window.location.host}`
      );
    }
    if (!this.cleanedParams.hasErrors) {
      this.initEhrMode(this.cleanedParams.params);
    }
    console.log("CS params: ", this.params);
  }

  initEhrMode(params: IClinicalSuiteIncomingParams) {
    const errorText = errorId => {
      switch (errorId) {
        case "UNAUTHORIZED":
          return "Vi kan ikke finde en bruger i DiaProfil som er konfiguret med de sendte oplysninger.<br/><br/>Kontakt venligst ZiteLab.";
        case "USER_NOT_FOUND":
          return "Vi kan ikke finde en bruger i DiaProfil som er konfiguret med de sendte oplysninger.<br/><br/>Kontakt venligst ZiteLab.";
        default:
          return "Der er sket en ukendt fejl, kontakt venligst ZiteLab";
      }
    };

    this.admin.triggerEhrMode(params).subscribe(result => {
      if (result.multiple) {
        const selectUserActionSheet = this.alertCtrl.create({
          title: "Vælg hospital/afdeling",
          message: `Du er tilknyttet eller har adgang til ${result.users.length} afdelinger/hospitaler. Vælg ønsket afdeling/hospital i listen nedenunder.`,
          cssClass: "customUserSelectAlert",
          enableBackdropDismiss: false,
          buttons: result.users.map(user => {
            return {
              text: user.organizationName,
              handler: () =>
                this.initEhrMode({
                  ...params,
                  diaprofilId: user.userId
                })
            };
          })
        });
        selectUserActionSheet.present();
      } else if (result.hasError) {
        const errorAlert = this.alertCtrl.create({
          title: "Fejl i opstart fra Clinical Suite",
          message: errorText(result.errorMsg),
          buttons: [
            {
              text: "OK",
              handler: () => {
                this.navCtrl.setRoot(FrontPage);
              }
            }
          ]
        });
        errorAlert.present();
      } else {
        // this.authService.setOrganizationConfig().subscribe();
        if (result.patient) {
          // We were provided a SSN by Clinical Suite and found a match at hospital
          this.goToPatientPage(
            result.patient.fhir_id,
            result.cs_user.organization_uid
          );
        } else {
          if (this.cleanedParams.params.ssn) {
            // We were provided a SSN by Clinical Suite but could not find a match at hospital
            this.navCtrl.setRoot(HomePage).then(result =>
              this.events.publish("trigger:managePatientModal", {
                prefilled: {
                  ssn:
                    this.cleanedParams.params.ssn !== undefined
                      ? this.cleanedParams.params.ssn
                      : null,
                  name:
                    this.cleanedParams.params.patientName !== undefined
                      ? this.cleanedParams.params.patientName
                      : null
                },
                showAlert: true
              })
            );
          } else {
            // No SSN provided by clinical suite, redirect to current consultations page
            this.navCtrl.setRoot(CurrentConsultationsPage);
          }
        }
      }
    });
  }

  cleanParams(
    string: string,
    required: string[] = []
  ): {
    params: IClinicalSuiteIncomingParams;
    hasErrors: boolean;
    errors: string[];
  } {
    const hashes = string.split("&");
    const params: IClinicalSuiteIncomingParams = {};
    hashes.map(hash => {
      const [key, val] = hash.split("=");
      params[key] = decodeURIComponent(val);
    });
    const errors = [];
    required.map(key => {
      if (!params.hasOwnProperty(key)) {
        errors.push(
          `Required parameter '${key}' from Clinical Suite for DiaProfil is missing`
        );
      }
    });
    return { params, hasErrors: errors.length > 0, errors };
  }

  goToPatientPage(selectedPatientId: string, curOrgId: string) {
    this.store
      .select(store => store.user)
      .pipe(first(user => user.definitionsLoaded === true))
      .subscribe(result =>
        this.admin.getUsers({
          userType: "Patient",
          organizationUuid: curOrgId,
          fhirId: selectedPatientId,
          extraAction: users => {
            this.admin.addPatientsToStore(users);
            this.store
              .select(state => state.patients)
              .pipe(
                first(
                  patients =>
                    patients.find(
                      patient =>
                        `Patient/${patient.fhirResource.id}` ===
                        selectedPatientId
                    ) !== undefined
                )
              )
              .subscribe(patients => {
                console.log(patients);
                const patient = patients.find(
                  patient =>
                    `Patient/${patient.fhirResource.id}` === selectedPatientId
                );
                const diaprofilPatient = new DiaprofilPatient(
                  patient,
                  this.translate,
                  this.questionnaireResponseService,
                  this.dashboard
                );
                this.navCtrl.setRoot(PatientPage, {
                  patient: diaprofilPatient
                });
              });
          }
        })
      );
  }
}



// WEBPACK FOOTER //
// ./src/pages/clinical-suite.page/clinical-suite.page.ts